<template>
  <div :id="computedId" class="ag-input-file">
    <div :class="computedClasses">
      <AGButton
        class="ag-input-file__fake-input-button"
        left-icon="md:drive_folder_upload"
        type="secondary"
        icon-size="22px"
        :style="`width: ${buttonWidth} !important`"
        :disabled="disabled"
        @click="triggerHiddenInput"
      >
        {{ localButtonText }}
      </AGButton>

      <template v-if="!multiple">
        <div class="ag-input-file__label">
          <AGTruncatedText class="ag-input-filefile__label" :text="computedLabel" />
          <AGButtonIcon
            v-if="hasFile"
            class="ag-input-file__fake-input-close"
            icon="mdi-close"
            size="md"
            @click="clearHiddenInput"
          />
        </div>
      </template>
    </div>

    <p v-if="hint" class="ag-input-file__orientacoes mb-0">
      {{ hint }}
    </p>

    <div v-if="multiple && hasFile" class="ag-input-file__files">
      <div v-for="file in localValue" :key="file.name" class="ag-input-file__file">
        <div class="ag-input-file__file-name">
          <AGTruncatedText class="ag-input-file__file-name" :text="file.name" />
        </div>
        <AGButtonIcon v-if="hasFile" icon="mdi-close" size="md" @click="removeFile(file.name)" />
      </div>
    </div>

    <v-file-input
      ref="hiddenInput"
      v-model="localValue"
      class="ag-input-file__input"
      hide-details="auto"
      :disabled="disabled"
      :rules="rules"
      :accept="accept"
      :multiple="multiple"
      :validate-on="validateOn"
    />
  </div>
</template>

<script>
import AGButton from '@/components/common/AGButton.vue';
import AGButtonIcon from '@/components/common/AGButtonIcon.vue';
import AGTruncatedText from '@/components/common/AGTruncatedText.vue';
import { uniqueId } from 'lodash';

export default {
  components: { AGButton, AGButtonIcon, AGTruncatedText },
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: [Array, File, Object],
      default: null,
    },
    id: {
      type: String,
      default: 'agInputFile',
    },
    buttonText: {
      type: String,
      default: 'Adicionar arquivo',
    },
    noDataText: {
      type: String,
      default: 'Nenhum arquivo selecionado',
    },
    accept: {
      type: String,
      default: 'image/*',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    buttonWidth: {
      type: String,
      default: 'auto',
    },
    rules: {
      type: Array,
      default: () => [],
    },
    validateOn: {
      type: String,
      default: 'input',
    },
    hint: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    isValid: true,
    errorMessages: [],
  }),

  computed: {
    localValue: {
      get() {
        return this.modelValue;
      },

      set(value) {
        this.$emit('update:modelValue', value);
      },
    },

    hasFile() {
      return this.multiple ? this.localValue?.length > 0 : !!this.localValue;
    },

    computedClasses() {
      let classes = 'ag-input-file__fake-input';

      if (this.disabled) classes += ' ag-input-file__fake-input--disabled';
      if (this.multiple) classes += ' ag-input-file__fake-input--multiple';
      if (!this.isValid) classes += ' ag-input-file__fake-input--invalid';
      if (!this.hasFile) classes += ' ag-input-file__fake-input--empty';

      return classes;
    },

    computedId() {
      return uniqueId(`${this.id}`);
    },

    computedLabel() {
      if (this.hasFile && this.multiple) {
        return `${this.localValue.length} arquivos selecionados`;
      } else if (this.hasFile) {
        return this.localValue.name;
      }

      return this.noDataText;
    },

    campoObrigatorio() {
      return this.rules.some((rule) => rule?.name?.toLowerCase().includes('required'));
    },

    localButtonText() {
      return `${this.buttonText}${this.campoObrigatorio ? ' *' : ''}`;
    },
  },

  methods: {
    triggerHiddenInput() {
      this.$refs.hiddenInput.click();
    },

    async clearHiddenInput() {
      // TODO: quando for refatorar o componente, não usar o v-file-input e ajustar essa função.
      await this.$refs.hiddenInput.reset();
      this.localValue = null;
      await this.$refs.hiddenInput.validate();
    },

    removeFile(fileName) {
      this.localValue = this.localValue.filter((file) => file.name !== fileName);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/messages.scss';

.ag-input-file {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  padding-bottom: $spacing-05;

  .ag-input-file__orientacoes {
    font-size: $font-size-xxs;
    color: $neutral-color-high-medium;
  }

  .ag-input-file__fake-input {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    border-radius: $border-radius-sm;
    background-color: $neutral-color-low-dark;

    &.ag-input-file__fake-input--invalid {
      background-color: $feedback-color-error-light;

      ::v-deep(.v-btn:not(.v-btn:hover)).ag-input-file__fake-input-close {
        color: $feedback-color-error-medium !important;
      }
    }

    &.ag-input-file__fake-input--multiple {
      background-color: transparent;
    }

    &:not(.ag-input-file__fake-input--disabled) {
      ::v-deep(.v-btn:not(.v-btn:hover)).ag-input-file__fake-input-button {
        background-color: #ffffff !important;
      }
    }

    &.ag-input-file__fake-input--empty {
      .ag-input-file__label {
        color: $neutral-color-high-light !important;
      }
    }

    .ag-input-file__label {
      flex: 1;
      display: flex;
      justify-content: space-between;
      align-items: center;
      overflow: hidden;
      text-wrap: nowrap;
      text-overflow: ellipsis;
      color: $brand-color-primary-medium;
      padding: $spacing-02 $spacing-03 $spacing-02 $spacing-04;
    }
  }

  .ag-input-file__files {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 8px;

    .ag-input-file__file {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 12px 16px;

      background-color: $neutral-color-low-dark;
      border-radius: 4px;

      .ag-input-file__file-name {
        min-width: 0;
        margin: 0;
      }
    }
  }

  ::v-deep(.v-input__prepend),
  ::v-deep(.v-input__control) {
    display: none;
  }
}
</style>
